import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Ticket, Settings2, UserX, Check, X as XIcon } from 'lucide-react';
import { Card, CardHeader } from '@/components/ui/Card';
import { Alert } from '@/components/ui/Alert';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { GuildChannelSelect } from '@/components/dashboard/GuildChannelSelect';
import { GuildRoleSelect } from '@/components/dashboard/GuildRoleSelect';
import { Modal } from '@/components/ui/Modal';
import { Table, TableHead, TableHeaderCell, TableBody } from '@/components/ui/Table';
import { Tabs } from '@/components/ui/Tabs';
import { ToggleRow } from '@/components/ui/ToggleRow';
import { useGuildConfig } from '@/hooks/useGuildConfig';
import { patchGuildConfig } from '@/lib/api-client/guild-config';
import { ApiError } from '@/lib/api-client/client';

/** Matches ticket_settings + the module_tickets toggle exactly
 * (bridge/config_registry.py's "tickets" module). */
interface TicketsData {
  enabled: boolean;
  category_id: string | null;
  log_channel: string | null;
  panel_channel: string | null;
  support_role: string | null;
}

export function TicketsPage() {
  const { guildId } = useParams<{ guildId: string }>();
  const { state, current, saveError, reload } = useGuildConfig<TicketsData>(guildId, 'tickets');
  const [tab, setTab] = useState('queue');
  const [setupOpen, setSetupOpen] = useState(false);
  // The modal edits its own local copy so closing without saving can't
  // leave a half-typed value sitting in the shared draft.
  const [form, setForm] = useState<TicketsData | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSaving, setModalSaving] = useState(false);

  useEffect(() => {
    if (setupOpen && current) setForm(current);
  }, [setupOpen, current]);

  async function handleSetupSave() {
    if (!form || !current || !guildId) return;
    // Diffed and sent directly (not through useGuildConfig's update()/save())
    // because those act on the hook's own async draft state, which
    // wouldn't yet reflect a change made in this same function call —
    // the modal keeps its own local `form` for exactly this reason.
    const changes: Partial<TicketsData> = {};
    (Object.keys(form) as (keyof TicketsData)[]).forEach((key) => {
      if (form[key] !== current[key]) changes[key] = form[key] as never;
    });
    if (Object.keys(changes).length === 0) {
      setSetupOpen(false);
      return;
    }
    setModalSaving(true);
    setModalError(null);
    try {
      await patchGuildConfig<TicketsData>(guildId, 'tickets', changes);
      reload();
      setSetupOpen(false);
    } catch (err) {
      setModalError(err instanceof ApiError ? err.message : 'Could not save — please try again.');
    } finally {
      setModalSaving(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-h1 text-text-primary mb-1">Tickets</h1>
        <p className="text-small text-text-secondary">Open ticket queue, setup, and blacklist.</p>
      </div>

      <Alert
        tone="info"
        title="Queue and blacklist aren't connected yet"
        description="Claim/close from the web needs a Bridge-callable version of the existing button-interaction logic. Setup below IS live — it reads and writes ticket_settings for real."
      />

      {saveError && <Alert tone="danger" title="Couldn't save" description={saveError} />}

      <Tabs
        tabs={[
          { id: 'queue', label: 'Active tickets' },
          { id: 'blacklist', label: 'Blacklist' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'queue' ? (
        <Card>
          <CardHeader
            title="Active tickets"
            action={
              <Button variant="secondary" size="sm" onClick={() => setSetupOpen(true)}>
                <Settings2 size={14} aria-hidden="true" />
                Setup
              </Button>
            }
          />
          <Table>
            <TableHead>
              <TableHeaderCell>Channel</TableHeaderCell>
              <TableHeaderCell>Opened by</TableHeaderCell>
              <TableHeaderCell>Claimed by</TableHeaderCell>
              <TableHeaderCell>Opened</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableHead>
            <TableBody />
          </Table>
          <div className="mt-2">
            <EmptyState
              icon={Ticket}
              title="Live queue isn't connected yet"
              description="Listing open tickets needs a Bridge read endpoint beyond this pass's configuration API."
              action={
                <div className="flex gap-2 opacity-50 pointer-events-none" aria-hidden="true">
                  <Button variant="secondary" size="sm"><Check size={14} /> Claim</Button>
                  <Button variant="ghost" size="sm"><XIcon size={14} /> Close</Button>
                </div>
              }
            />
          </div>
        </Card>
      ) : (
        <Card>
          <CardHeader title="Blacklist" description="Members blocked from opening tickets." action={<Button variant="secondary" size="sm" disabled><UserX size={14} aria-hidden="true" />Add member</Button>} />
          <EmptyState icon={UserX} title="Not connected yet" description="Blacklist management needs a dedicated Bridge endpoint beyond this pass." />
        </Card>
      )}

      <Modal
        open={setupOpen}
        onClose={() => { setSetupOpen(false); setModalError(null); }}
        title="Ticket setup"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSetupOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSetupSave} disabled={modalSaving || state.kind !== 'ready'}>
              {modalSaving ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        {state.kind === 'loading' && <p className="text-small text-text-secondary">Loading…</p>}
        {state.kind === 'forbidden' && <Alert tone="danger" title="You don't have permission" description="Ticket setup requires Administrator." />}
        {state.kind === 'unavailable' && <Alert tone="danger" title="Couldn't load settings" description={state.message} />}
        {form && state.kind === 'ready' && (
          <div className="space-y-3">
            {modalError && <Alert tone="danger" title="Couldn't save" description={modalError} />}
            <ToggleRow title="Enable ticket system" checked={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} />
            <div>
              <label className="text-small text-text-secondary block mb-1">Ticket category</label>
              <GuildChannelSelect
                guildId={guildId}
                value={form.category_id}
                onChange={(id) => setForm({ ...form, category_id: id })}
                types={['category']}
                aria-label="Ticket category"
              />
            </div>
            <div>
              <label className="text-small text-text-secondary block mb-1">Log channel</label>
              <GuildChannelSelect
                guildId={guildId}
                value={form.log_channel}
                onChange={(id) => setForm({ ...form, log_channel: id })}
                aria-label="Log channel"
              />
            </div>
            <div>
              <label className="text-small text-text-secondary block mb-1">Panel channel</label>
              <GuildChannelSelect
                guildId={guildId}
                value={form.panel_channel}
                onChange={(id) => setForm({ ...form, panel_channel: id })}
                aria-label="Panel channel"
              />
            </div>
            <div>
              <label className="text-small text-text-secondary block mb-1">Support role</label>
              <GuildRoleSelect
                guildId={guildId}
                value={form.support_role}
                onChange={(id) => setForm({ ...form, support_role: id })}
                aria-label="Support role"
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
